<script type="ts" setup>
import {
  ref, computed, toRaw, watch, onMounted,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';

import { ElLink, ElTabs, ElTabPane } from 'element-plus';
import AppMainContent from '@/components/AppMainContent.vue';
import ActivityTable from '@/views/activity/ActivityTable.vue';
import ActivityTableFilters from '@/views/activity/ActivityTableFilters.vue';
import SelectOrganizationWarning from '@/components/SelectOrganizationWarning.vue';
import shared from '../manage-users-and-organizations/shared';

const store = useStore();
const router = useRouter();
const isOrgAdmin = store.getters['users/isOrgAdmin'];
const route = useRoute();

const { doGetStripeAuthorizeUrl, getOrganization } = shared();

const showSelectOrganization = ref(false);
const activeTransactionTab = ref('sticker');

const selectedOrganization = computed(() => store.getters['organizations/GET_SELECTED_ORGANIZATION']);

// Lazy load PDF libraries
let jsPDF = null;
let autoTable = null;

const loadPDFLibraries = async () => {
  if (!jsPDF) {
    const jsPDFModule = await import('jspdf');
    jsPDF = jsPDFModule.default;
    await import('jspdf-autotable');
    autoTable = jsPDF.prototype.autoTable;
  }
};

const fetchActivityData = async (orgId) => {
  // Clear all transaction data
  store.commit('activityLogs/MUTATE_STICKER_TRANSACTIONS', []);
  store.commit('activityLogs/MUTATE_STRIPE_TRANSACTIONS', []);
  store.commit('activityLogs/MUTATE_BENEVITY_TRANSACTIONS', []);
  store.commit('activityLogs/MUTATE_BENEVITY_W2W_TRANSACTIONS', []);
  store.dispatch('activityLogs/SET_SELECTED_LOGS', []);

  // Reset pagination
  store.commit('activityLogs/MUTATE_PAGINATION', { skip: 0, take: 8 });

  // Fetch data for the current tab
  await store.dispatch('activityLogs/FETCH_CURRENT_TAB_DATA');
};

const updateSelectOrgWarning = () => {
  if (isOrgAdmin) {
    if ((!route.params.id || route.params.id === ':id' || route.params.id === 'ORG_ID' || route.params.id === 'undefined')
          && (!selectedOrganization.value || !selectedOrganization.value.id)) {
      showSelectOrganization.value = true;
    } else {
      showSelectOrganization.value = false;

      if (route.params.id && route.params.id !== ':id' && route.params.id !== 'ORG_ID' && route.params.id !== 'undefined') {
        fetchActivityData(route.params.id);
      } else if (selectedOrganization.value && selectedOrganization.value.id) {
        fetchActivityData(selectedOrganization.value.id);
      }
    }
  } else {
    fetchActivityData();
    showSelectOrganization.value = false;
  }
};

onMounted(() => {
  updateSelectOrgWarning();
});

watch(selectedOrganization, (newOrg, oldOrg) => {
  const newOrgId = newOrg?.id;
  const oldOrgId = oldOrg?.id;

  if (newOrgId !== oldOrgId) {
    if (isOrgAdmin && newOrgId) {
      if (route.path.includes('/activity/')) {
        const newPath = `/activity/${newOrgId}`;
        if (route.path !== newPath) {
          router.push(newPath);
        } else {
          fetchActivityData(newOrgId);
        }
      } else {
        updateSelectOrgWarning();
      }
    } else if (!isOrgAdmin) {
      fetchActivityData();
    } else {
      updateSelectOrgWarning();
    }
  }
});

watch(() => route.params.id, (newId, oldId) => {
  if (newId !== oldId && newId && newId !== ':id' && newId !== 'ORG_ID' && newId !== 'undefined') {
    fetchActivityData(newId);
  }
});

const selectedRows = computed(() => store.getters['activityLogs/GET_SELECTED_LOGS']);

const isExporting = ref(false);
const onExport = async () => {
  isExporting.value = true;

  try {
    // Load PDF libraries on demand
    await loadPDFLibraries();

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'pt',
      format: 'A4',
    });

    const tableColumn = [
      { header: 'Transaction', dataKey: 'transactionId' },
      { header: 'Date', dataKey: 'date' },
      { header: 'User', dataKey: 'userName' },
      { header: 'Total', dataKey: 'amount' },
      { header: 'Item', dataKey: 'item' },
      { header: 'Details', dataKey: 'details' },
      { header: 'Email', dataKey: 'email' },
      { header: 'Type', dataKey: 'type' },
    ];

    const tableRows = selectedRows.value.map((row) => ({
      transactionId: row.transactionId.slice(-5),
      date: new Date(row.date).toLocaleString(),
      userName: row.userName,
      amount: row.amount.toFixed(2),
      item: row.item,
      details: row.details,
      email: row.email,
      type: row.type,
    }));

    doc.autoTable({
      columns: tableColumn,
      body: tableRows,
      styles: {
        fontSize: 10,
        overflow: 'linebreak',
        cellPadding: 5,
      },
      columnStyles: {
        transactionId: { cellWidth: 80 },
        date: { cellWidth: 100 },
        userName: { cellWidth: 70 },
        amount: { cellWidth: 50 },
        item: { cellWidth: 60 },
        details: { cellWidth: 120 },
        email: { cellWidth: 120 },
        type: { cellWidth: 70 },
      },
      margin: { top: 60 },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
    });

    const now = new Date();
    const dateTimeString = now.toISOString().slice(0, 19).replace(/:/g, '-');
    const fileName = `activity_logs_${dateTimeString}.pdf`;

    doc.save(fileName);
  } catch (error) {
    console.error('Error generating PDF:', error);
  } finally {
    isExporting.value = false;
  }
};

const onExportCSV = () => {
  const headers = [
    'Transaction',
    'Date',
    'User',
    'Total',
    'Item',
    'Details',
    'Email',
    'Type',
  ];
  const selectedLogs = toRaw(store.getters['activityLogs/GET_SELECTED_LOGS']);
  const currentTransactions = toRaw(store.getters['activityLogs/GET_CURRENT_TRANSACTIONS']);
  const rows = selectedLogs?.length > 0 ? selectedLogs : currentTransactions;
  const csvContent = [
    headers.join(','),
    ...rows.map((row) => {
      const transId = `#${row.transactionId.slice(-5).toString()}`;
      const values = [
        transId,
        new Date(row.date).toLocaleString(),
        row.userName,
        row.amount.toFixed(2),
        row.item,
        row.details,
        row.email,
        row.type,
      ];
      return values.map((value) => `"${value}"`).join(',');
    }),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const now = new Date();
  const dateTimeString = now.toISOString().slice(0, 19).replace(/:/g, '-');
  const fileName = `activity_logs_${dateTimeString}.csv`;

  if (navigator.msSaveBlob) {
    navigator.msSaveBlob(blob, fileName);
  } else {
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  isExporting.value = false;
};

async function onBoardOnStripe() {
  const organization = await getOrganization(route.params.id);
  if (!organization.stripe_onboarded && route.params.id) {
    const stripeLink = await doGetStripeAuthorizeUrl(route.params.id);
    window.location.href = stripeLink;
  } else {
    window.location.href = 'https://connect.stripe.com/';
  }
}

// Handle transaction tab changes
const handleTransactionTabChange = (tabName) => {
  activeTransactionTab.value = tabName;
  store.dispatch('activityLogs/SET_CURRENT_TAB', tabName);
};
</script>

<template>
  <app-main-content>
    <section class="c-activity-logs d-flex justify-content-between align-items-center">
      <h1 class="c-activity-logs__heading">Activity</h1>
      <el-button
        class="el-button el-button--large el-button--success c-activity-logs__button"
        @click="onExportCSV"
        :loading="isExporting"
      >
        Export
      </el-button>
    </section>

    <SelectOrganizationWarning v-if="showSelectOrganization" type="logs" />

    <section v-else class="bg-white rounded pb-4 c-activity-logs-table">
      <article class="px-4 py-4 border-bottom d-flex justify-content-between align-items-center">
        <div class="d-flex gap-4">
          <el-link :underline="false" class="fs-20 fw-bold text-black fs-20">Transactions</el-link>
          <template v-if="!isOrgAdmin">
            <el-link :underline="false" class="fs-20 fw-bold text-black-50" href="/manage-resources/todos">Resources</el-link>
            <el-link :underline="false" class="fs-20 fw-bold text-black-50" href="/manage-content/events">Events</el-link>
            <el-link :underline="false" class="fs-20 fw-bold text-black-50" href="/manage-content/directory/products">Products</el-link>
            <el-link :underline="false" class="fs-20 fw-bold text-black-50" href="/manage-content/directory/roles">Roles</el-link>
            <el-link :underline="false" class="fs-20 fw-bold text-black-50" href="/manage-content/directory/security">Security</el-link>
          </template>
        </div>
        <div class="d-flex gap-4">
          <el-link href="https://causes.benevity.org/" target="_blank" :underline="false" class="text-success fw-bold fs-6" v-if="isOrgAdmin">Benevity Payouts</el-link>
          <el-link @click="onBoardOnStripe" :underline="false" class="text-success fw-bold fs-6">Stripe Account</el-link>
        </div>
      </article>

      <!-- Transaction Sub-tabs -->
      <article class="px-4 py-2">
        <el-tabs
          v-model="activeTransactionTab"
          @tab-change="handleTransactionTabChange"
          class="c-transaction-tabs"
        >
          <el-tab-pane label="Sticker Transactions" name="sticker" />
          <el-tab-pane label="Stripe Transactions" name="stripe" />
          <el-tab-pane label="Benevity Transactions" name="benevity" />
          <el-tab-pane label="Benevity W2W Credit Transactions" name="benevity-w2w" />
        </el-tabs>
      </article>

      <article class="px-4 pt-2 pb-4">
        <ActivityTableFilters />
      </article>
      <ActivityTable />
    </section>

  </app-main-content>
</template>

<style lang="scss">
.c-activity-logs {
  margin-bottom: 19px;
  &-table {
    .el-checkbox {
      &__label {
        display:none;
      }
    }
  }

  &__heading {
    font-size: 40px;
    margin-bottom: 0;
  }

  &__button {
    height: 40px;
    width: 94px;
    font-size: 16px;
    span {
      font-weight: 700;
    }
  }
}

.c-transaction-tabs {
  .el-tabs__header {
    margin: 0;
  }

  .el-tabs__nav-wrap {
    &::after {
      height: 1px;
      background-color: #e4e7ed;
    }
  }

  .el-tabs__item {
    padding: 0 20px;
    height: 40px;
    line-height: 40px;
    font-size: 14px;
    font-weight: 500;
    color: #606266;

    &.is-active {
      color: #409eff;
      font-weight: 600;
    }

    &:hover {
      color: #409eff;
    }
  }

  .el-tabs__active-bar {
    height: 2px;
    background-color: #409eff;
  }
}
</style>
